import { BoxGeometry, Mesh, MeshStandardMaterial, type Group, type Material, type Texture } from 'three';
import { flatMaterial } from '../../render/materials/cache';
import { MODULE } from './facadePainters';
import { facadeTexture, moduleWidth, type FacadeKind } from './facadeLibrary';

/** Unit box with its six faces grouped into three draws: street faces, roof/floor, side faces. */
function groupedBox(): BoxGeometry {
  const box = new BoxGeometry(1, 1, 1);
  box.clearGroups();
  box.addGroup(0, 12, 0);
  box.addGroup(12, 12, 1);
  box.addGroup(24, 12, 2);
  return box;
}

const UNIT = groupedBox();

export interface BuildingLook {
  upper: FacadeKind;
  ground: FacadeKind;
  variant: number;
  groundVariant: number;
  floors: number;
  /** Frontage along the street and depth away from it, metres. */
  width: number;
  depth: number;
  groundHeight: number;
  roof: string;
}

export interface SlotPlacement {
  z: number;
  groundX: number;
  groundDepth: number;
  upperX: number;
  upperBase: number;
  /** Narrow the upper box (villas are smaller than their plots). */
  upperInset?: number;
}

type FaceMaterials = [front: Material, roof: Material, side: Material];

function faceMaterials(kind: FacadeKind, variant: number, width: number, depth: number, rows: number, roof: string): FaceMaterials {
  const glassy = kind === 'glass';
  const make = (repeatX: number): Material => {
    const map = facadeTexture(kind, variant, repeatX, rows);
    return glassy ? new MeshStandardMaterial({ map, roughness: 0.2, metalness: 0.5 }) : new MeshStandardMaterial({ map, roughness: 0.9 });
  };
  const front = make(Math.max(1, Math.round(width / moduleWidth(kind))));
  const side = make(Math.max(1, Math.round(depth / moduleWidth(kind))));
  const top = flatMaterial(roof);
  return [front, top, side];
}

function disposeFaces(mesh: Mesh): void {
  const materials = mesh.material as Material[];
  for (const material of new Set(materials)) {
    if (material instanceof MeshStandardMaterial) {
      if (material.map && !material.userData.sharedMap) material.map.dispose();
      if (material.map) material.dispose();
    }
  }
}

/** A building with a real photo frontage. Painted buildings are merged per street side instead (streetside.ts). */
export class BuildingSlot {
  readonly ground = new Mesh(UNIT, flatMaterial('#ccc'));
  readonly upper = new Mesh(UNIT, flatMaterial('#ccc'));

  constructor(parent: Group) {
    for (const mesh of [this.ground, this.upper]) {
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      parent.add(mesh);
    }
  }

  hide(): void {
    this.ground.visible = false;
    this.upper.visible = false;
  }

  /** A building whose street face is a real photo; the other faces are painted to match. */
  dressPhoto(photo: Texture, look: BuildingLook, height: number, at: SlotPlacement): void {
    const painted = faceMaterials(look.upper, look.variant, look.width, look.depth, Math.max(1, Math.round(height / MODULE.height)), look.roof);
    const front = new MeshStandardMaterial({ map: photo });
    this.swap(this.ground, [front, painted[1], painted[2]], true);
    this.ground.scale.set(at.groundDepth, height, look.width);
    this.ground.position.set(at.groundX, height / 2, at.z);
    this.ground.visible = true;
    this.upper.visible = false;
  }

  private swap(mesh: Mesh, materials: FaceMaterials, sharedFront = false): void {
    if (sharedFront) {
      // The photo texture is shared by every building using it: only dispose the material, not the map.
      if (Array.isArray(mesh.material)) disposeFaces(mesh);
      mesh.material = materials;
      (materials[0] as MeshStandardMaterial).userData.sharedMap = true;
      return;
    }
    if (Array.isArray(mesh.material)) disposeFaces(mesh);
    mesh.material = materials;
  }
}
